import { uniqBy } from 'lodash';
import type { FieldPolicy, Reference, StoreObject } from '@apollo/client/cache';

type KeyArgs = FieldPolicy<any>['keyArgs'];

/**
 * Field policy for offset-based pagination (mode: OFFSET with afterOffset).
 * Merges paginated results by placing incoming nodes at the specified offset position.
 *
 * @param keyArgs - Arguments that determine cache key uniqueness (e.g., filters, search terms)
 * @param idField - Field name to use for deduplication (defaults to 'id')
 * @returns A field policy with keyArgs and merge function for offset pagination
 */
export function offsetPaginationFieldPolicy(
  keyArgs: KeyArgs = false,
  idField: string = 'id'
): Pick<FieldPolicy, 'keyArgs' | 'merge'> {
  return {
    keyArgs,
    merge(
      existing = {
        nodes: [],
        pageInfo: {
          hasNextPage: true,
          hasPreviousPage: false,
        },
        totalCount: 0,
      },
      incoming,
      { args, isReference, readField }
    ) {
      const getId = (node: Reference | StoreObject) =>
        node && isReference(node) ? readField(idField, node) : node?.[idField];

      const mergedNodes = existing.nodes ? existing.nodes.slice(0) : [];

      if (incoming?.nodes) {
        const { afterOffset = 0 } = args?.page || {};
        for (let i = 0; i < incoming.nodes.length; i += 1) {
          const incomingNode = incoming.nodes[i];
          mergedNodes[afterOffset + i] = incomingNode;
        }
      }

      const nodes = uniqBy(mergedNodes, (node) =>
        getId(node as Reference | StoreObject)
      );

      return {
        ...existing,
        ...incoming,
        nodes,
      };
    },
  };
}
